// ** React Imports
import { ReactNode, useCallback, useEffect, useState } from 'react'
import { useRouter } from 'next/router'

// ** Hooks
import { useAuth } from 'src/hooks/useAuth'

// ** Layout Import
import BlankLayout from 'src/@core/layouts/BlankLayout'
import Spinner from 'src/@core/components/spinner'
import {
  Alert,
  Button,
  Grid,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material'

// ** Iframe Utils
import { isIframeContext, isIframeRequest } from '@/src/utils/iframeUtils'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@/src/store'
import { RestoUser, fetchCurrentUser } from '@/src/store/apps/resto365UserManagement'
import { AUTH_LOGIN_ERROR_KEY, handleLoginError } from '@/src/@core/utils/handle-login-error'
import { Country, CountryNameMap } from '@/src/types/apps/resto365Countries'
import oktaAuth from '@/src/services/okta'
import { setSessionCountry, setSessionCountryFromStorage } from '@/src/@core/lib/session'
import { Icon } from '@iconify/react'
import { usePageTracking, useEventTracking } from '@/src/googleAnalytics/analyticsModule'

enum LoginStatus {
  LoggedIn,
  LoggedOut,
  Indeterminate
}

const LoginPage = () => {
  const router = useRouter()
  const auth = useAuth()
  const theme = useTheme()
  const [isLoggedIn, setIsLoggedIn] = useState<LoginStatus>(LoginStatus.Indeterminate)
  const dispatch = useDispatch<AppDispatch>()

  // Check if this is an iframe context
  const isIframe = isIframeContext() || isIframeRequest()
  const {
    currentUser: { value: currentUser, error: errorCurrentUser }
  } = useSelector((state: RootState) => state.resto365UserManagement)
  const [country, setCountry] = useState<Country>(Country.AUS)
  const [authErrorMessage, setAuthErrorMessage] = useState<string>('')

  const checkLoggedIn = useCallback(async () => {
    try {
      const isAuthenticated = await oktaAuth.isAuthenticated()
      setSessionCountryFromStorage()
      if (isAuthenticated) {
        dispatch(fetchCurrentUser())
      } else {
        setIsLoggedIn(LoginStatus.LoggedOut)
      }
    } catch (err: any) {
      await handleLoginError(auth, err)
    }
  }, [setIsLoggedIn, dispatch, auth])

  const handleLogin = useCallback(
    async (currentUser: RestoUser) => {
      if (currentUser) {
        await auth.handleLogin({
          id: currentUser.id,
          email: currentUser.email!,
          role: currentUser.role,
          fullName: currentUser.name,
          username: currentUser.name,
          permissions: currentUser.permissions,
          countries: currentUser.countries,
          restaurants: currentUser.restaurants
        })
        setIsLoggedIn(LoginStatus.LoggedIn)
      }
    },
    [auth, setIsLoggedIn]
  )

  useEffect(() => {
    if (errorCurrentUser) {
      handleLoginError(auth, errorCurrentUser)
    }
  }, [errorCurrentUser, auth])

  useEffect(() => {
    checkLoggedIn()
  }, [checkLoggedIn])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const message = window.sessionStorage.getItem(AUTH_LOGIN_ERROR_KEY)
    if (message) {
      setAuthErrorMessage(message)
      window.sessionStorage.removeItem(AUTH_LOGIN_ERROR_KEY)
    }
  }, [])

  // Track page view using the hook
  usePageTracking('Login to Bhyve')

  // Get event tracking functions
  const { trackButtonClick } = useEventTracking()

  useEffect(() => {
    if (currentUser) {
      handleLogin(currentUser)
    }
  }, [currentUser, handleLogin])

  const onSignIn = async () => {
    // Track login button click
    trackButtonClick('login_with_okta', '/login')

    try {
      setSessionCountry(country)

      // Preserve returnUrl across Okta redirect (callback page won't have it in the URL)
      const returnUrl = router.query.returnUrl
      if (typeof returnUrl === 'string' && returnUrl) {
        sessionStorage.setItem('auth.returnUrl', returnUrl)
      }
      await oktaAuth.signInWithRedirect()
    } catch (err: any) {
      await handleLoginError(auth, err)
    }
  }
  const isTablet = useMediaQuery('(max-width:1024px)')

  const isTabletBackground = {
    backgroundImage: 'url(/images/logos/login_bg.png)', // Replace /path/to/your/image.jpg with the actual path to your image
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    height: '100vh', // Optional: Set the height to full viewport height
    width: '100%' // Optional: Set the width to full width
  }

  const authErrorSnackbar = (
    <Snackbar
      open={Boolean(authErrorMessage)}
      autoHideDuration={7000}
      onClose={() => setAuthErrorMessage('')}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Alert onClose={() => setAuthErrorMessage('')} severity='error' variant='filled' sx={{ width: '100%' }}>
        {authErrorMessage}
      </Alert>
    </Snackbar>
  )

  return isLoggedIn === LoginStatus.LoggedOut ? (
    <>
      {authErrorSnackbar}
      {isIframe ? (
        <Grid
          container
          justifyContent='center'
          alignItems='center'
          className='login-page'
          sx={{
            minHeight: '600px',
            height: '600px',
            width: '100%',
            padding: 2,
            backgroundColor: '#f5f5f5'
          }}
        >
          <Stack
            direction='column'
            gap={3}
            alignItems='center'
            justifyContent={'center'}
            className='login-form'
            sx={{
              width: '100%',
              maxWidth: 400,
              height: '100%',
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: 4,
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
            }}
          >
            <img src='/images/logos/gyg-logo.png' height={80} alt='GYG logo' />

            <Typography
              sx={{
                fontFamily: 'Sinibold',
                fontSize: '32px',
                fontWeight: 700,
                lineHeight: '1.2',
                textAlign: 'center',
                marginBottom: 2
              }}
            >
              Login to Bhyve
            </Typography>

            <Select
              fullWidth
              labelId='select-country'
              name='country'
              value={country}
              sx={{ width: '100%', textAlign: 'center', marginBottom: 2 }}
              onChange={e => setCountry(e.target.value as Country)}
            >
              <MenuItem value='au'>🇦🇺 {CountryNameMap[Country.AUS]}</MenuItem>
              <MenuItem value='us'>🇺🇸 {CountryNameMap[Country.USA]}</MenuItem>
            </Select>

            <Button
              onClick={onSignIn}
              startIcon={<Icon icon='ic:twotone-trip-origin' color='#3173BC' width={20} />}
              sx={{
                color: theme.palette.text.primary,
                background: theme.palette.primary.main,
                fontFamily: 'Montserrat !important',
                fontSize: '16px',
                fontWeight: 'bold',
                padding: '12px 24px',
                width: '100%',
                ' &:hover': {
                  background: theme.palette.primary.main
                }
              }}
            >
              Login with Okta
            </Button>
          </Stack>
        </Grid>
      ) : isTablet ? (
        <Grid container justifyContent='center' alignItems='center' wrap='nowrap' style={isTabletBackground}>
          <Stack
            direction='column'
            gap={10}
            alignItems='center'
            justifyContent={'center'}
            sx={{
              width: 700,
              height: 700,
              backgroundColor: 'rgba(255, 255, 255,1)',
              borderRadius: '24px'
            }}
          >
            <img src='/images/logos/gyg-logo.png' height={140} alt='GYG logo' />

            <Typography
              sx={{
                fontFamily: 'Sinibold',
                fontSize: '64px !important',
                fontWeight: 700,
                lineHeight: '22px',
                padding: 5
              }}
            >
              Login to Bhyve
            </Typography>
            <Select
              fullWidth
              labelId='select-country'
              name='country'
              value={country}
              sx={{ width: 600, textAlign: 'center' }}
              onChange={e => setCountry(e.target.value as Country)}
            >
              <MenuItem value='au'>🇦🇺 {CountryNameMap[Country.AUS]}</MenuItem>
              <MenuItem value='us'>🇺🇸 {CountryNameMap[Country.USA]}</MenuItem>
            </Select>

            <Button
              onClick={onSignIn}
              startIcon={<Icon icon='ic:twotone-trip-origin' color='#3173BC' width={24} />}
              sx={{
                color: theme.palette.text.primary,
                background: theme.palette.primary.main,
                fontFamily: 'Montserrat !important',
                fontSize: '20px',
                fontWeight: 'bold',
                padding: 5,
                width: 600,
                ' &:hover': {
                  background: theme.palette.primary.main
                }
              }}
            >
              Login with Okta
            </Button>
          </Stack>
        </Grid>
      ) : (
        <Grid container justifyContent='center' alignItems='center' wrap='nowrap' padding={4} height={'100vh'}>
          <Grid
            item
            xs={12}
            md={6}
            lg={6}
            width={'100vw'}
            height={'100%'}
            display={'flex'}
            alignItems={'center'}
            justifyContent={'center'}
            overflow={'hidden'}
            borderRadius={'40px'}
          >
            <img
              src='/images/logos/login_bg.png'
              alt='GYG Background'
              style={{ objectFit: 'fill', width: 'auto', height: '100%' }}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={6}>
            <Stack direction='column' gap={5} alignItems='center' justifyContent={'center'}>
              <img src='/images/logos/gyg-logo.png' height={140} alt='GYG logo' />

              <Typography
                sx={{
                  fontFamily: 'Sinibold',
                  fontSize: '64px',
                  fontWeight: 700,
                  lineHeight: '22px',
                  padding: 5
                }}
              >
                Login to Bhyve
              </Typography>
              <Select
                fullWidth
                labelId='select-country'
                name='country'
                value={country}
                sx={{ width: 600, textAlign: 'center' }}
                onChange={e => setCountry(e.target.value as Country)}
              >
                <MenuItem value='au'>🇦🇺 {CountryNameMap[Country.AUS]}</MenuItem>
                <MenuItem value='us'>🇺🇸 {CountryNameMap[Country.USA]}</MenuItem>
              </Select>

              <Button
                onClick={onSignIn}
                startIcon={<Icon icon='ic:twotone-trip-origin' color='#3173BC' width={24} />}
                sx={{
                  color: theme.palette.text.primary,
                  background: theme.palette.primary.main,
                  fontFamily: 'Montserrat !important',
                  fontSize: '20px',
                  fontWeight: 'bold',
                  padding: 5,
                  width: 600,
                  ' &:hover': {
                    background: theme.palette.primary.main
                  }
                }}
              >
                Login with Okta
              </Button>
            </Stack>
          </Grid>
        </Grid>
      )}
    </>
  ) : (
    <>
      {authErrorSnackbar}
      <Spinner />
    </>
  )
}

LoginPage.getLayout = (page: ReactNode) => <BlankLayout>{page}</BlankLayout>

LoginPage.guestGuard = true

export default LoginPage
